import { Box, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

const useStyles = makeStyles(({ spacing, typography }) => ({
  root: {
    height: '100%'
  },
  container: {
    width: '100%',
    alignItems: 'flex-start',
    paddingTop: spacing(2)
  },
  imageContanier: {
    height: '80px',
    width: '107px',
    marginLeft: spacing(2),
    flexShrink: 0
  },
  image: {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  contentContainer: {
    height: '100%',
    justifyContent: 'flex-start',
    paddingBottom: 0,
    paddingTop: 0
  },
  // `&&` doubles the class selector (`.cardTitle.cardTitle`), bumping
  // specificity above card-content.js's base `cardTitle` class. Without
  // this, base + override classes have equal specificity and source order
  // wins — which goes to the base because parent useStyles is inserted
  // before child useStyles in the React render tree.
  cardTitle: {
    '&&': {
      ...typography.heading.h6
    }
  },
  cardSummary: {
    '&&': {
      ...typography.body.s,
      fontWeight: typography.weight.regular,
      lineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  cardContentType: {
    ...typography.body.s
  },
  actions: {
    marginTop: 'auto',
    marginBottom: spacing(2)
  },
  time: {
    ...typography.body.s
  }
}))

const PromotedContentGenericCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, ...props }) => {
  const classes = useStyles()
  const {
    contentImage,
    handleCardSelect,
    cardSize,
    hoverShadow,
    actions
  } = usePromotedCardMethods({ content, position, size, cardType: PROMOTED_CARD_TYPES.GENERIC, onSelect, onAddToPlaylist })

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardSelect()
    }
  }

  return (
    <PromotedCardContainer
      size={cardSize}
      hoverShadow={hoverShadow}
      actions={actions}
      content={content}
      testId={testId}
    >
      <Box display='flex' flexDirection='row' className={classes.container}>
        <Box
          className={classes.imageContanier}
          onClick={handleCardSelect}
          onKeyDown={handleImageKeyDown}
          role='button'
          tabIndex={0}
          aria-label={content ? `View ${content.title}` : 'Loading content'}
          sx={{
            cursor: 'pointer',
            '&:focus': {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
              borderRadius: '8px'
            }
          }}
        >
          {
              content
                ? (
                  <img
                    src={contentImage}
                    title={content?.image?.altText}
                    className={classes.image}
                    data-test={`card-image-${content?.friendlyUrl}`}
                    alt={content?.image?.altText || content?.title}
                  />
                  )
                : (
                  <Skeleton variant='rectangular' className={classes.image} />
                  )
            }
        </Box>
        <PromotedContentCardContent
          content={content}
          actions={actions}
          cardType={PROMOTED_CARD_TYPES.GENERIC}
          defaultSectionMargin={1}
          onSelect={handleCardSelect}
          overrides={{
            contentContainer: classes.contentContainer,
            cardTitle: classes.cardTitle,
            cardSummary: classes.cardSummary,
            cardContentTypeText: classes.cardContentType,
            actions: classes.actions,
            time: classes.time
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentGenericCard
